import { useCallback, useEffect, useRef, useState } from "react";
import { useRoomContext } from "@livekit/components-react";
import { DisconnectReason, MediaDeviceFailure, RoomEvent } from "livekit-client";
import { useMutation } from "@tanstack/react-query";
import Axios, { AxiosError } from "axios";
import { AdmSupportVideoCallsApi } from "@/custolink-transpiled_by_server/bc.asset_interaction.api_methods";

export type VideoRoomRole = "worker" | "customer";

export type VideoRoomEndedInfo = {
  reason?: DisconnectReason;
  /** True when the other side terminated the call rather than this browser. */
  remoteClose: boolean;
};

/**
 * Ensures camera and microphone tracks are enabled as soon as the room
 * connects. LiveKitRoom already calls setCameraEnabled/setMicrophoneEnabled
 * on SignalConnected, but the getUserMedia call can fail silently when the
 * browser permission prompt has not yet been resolved. This component
 * subscribes to the Connected event and calls enableCameraAndMicrophone() as a
 * safety net so that tracks are active immediately when permissions are
 * already granted.
 *
 * `enabled` defaults to true. A participant whose token withholds publishing (D-06, the recording
 * consent hold) mounts it with enabled={false}: the Connected handler then does nothing, and the
 * tracks come up through the consent grant path instead.
 */
export function AutoEnableTracks(p: { onFailure: (failure: MediaDeviceFailure) => void; enabled?: boolean }) {
  const room = useRoomContext();
  const onFailure = p.onFailure;
  const enabled = p.enabled !== false;

  useEffect(() => {
    if (!enabled) {
      return;
    }
    const handleConnected = () => {
      const { localParticipant } = room;
      if (
        !localParticipant.isCameraEnabled ||
        !localParticipant.isMicrophoneEnabled
      ) {
        localParticipant.enableCameraAndMicrophone().catch((err) => {
          // The browser may still be showing its permission prompt, in which
          // case the tracks come up through the normal flow. A hard refusal or
          // a missing device is reported so the user gets told what to do.
          const failure = MediaDeviceFailure.getFailure(err);
          if (failure != null) {
            onFailure(failure);
          }
        });
      }
    };

    room.on(RoomEvent.Connected, handleConnected);
    return () => {
      room.off(RoomEvent.Connected, handleConnected);
    };
  }, [room, onFailure, enabled]);

  return null;
}

export function mediaFailureMessage(failure: MediaDeviceFailure) {
  switch (failure) {
    case MediaDeviceFailure.PermissionDenied:
      return {
        title: "Camera and microphone are blocked",
        body: "Your browser is blocking access. Allow camera and microphone for this site - on a phone, tap the lock or camera icon next to the address - and then try again.",
      };
    case MediaDeviceFailure.NotFound:
      return {
        title: "No camera or microphone found",
        body: "We could not find a camera or microphone on this device. Connect one and try again.",
      };
    case MediaDeviceFailure.DeviceInUse:
      return {
        title: "Camera or microphone is busy",
        body: "Another application is using your camera or microphone. Close it and try again.",
      };
    default:
      return {
        title: "Camera and microphone unavailable",
        body: "We could not start your camera and microphone. Check your device settings and try again.",
      };
  }
}

/**
 * Turns an End session failure into the text shown to the user. A 422 with
 * validation messages (e.g. "a customer is still in this call") is shown
 * verbatim, same as everywhere else in the app; anything else gets a
 * generic message.
 */
export function endSessionErrorMessage(err: unknown): string {
  if (Axios.isAxiosError(err)) {
    const axiosErr = err as AxiosError;
    if (axiosErr.status == 422) {
      const data = axiosErr.response?.data as {
        validationMessages?: Record<string, string> | null;
      };
      if (data.validationMessages != null) {
        const msgs = Object.values(data.validationMessages);
        if (msgs.length > 0) {
          return msgs.join(", ");
        }
      }
    }
  }
  return "Could not end the session.";
}

export type VideoRoomConnection = {
  attempt: number;
  handleConnected: () => void;
  handleDisconnected: (reason?: DisconnectReason) => void;
  handleError: (err: Error) => void;
  handleMediaDeviceFailure: (failure?: MediaDeviceFailure) => void;
  /** Stable setter, safe to pass directly as AutoEnableTracks.onFailure. */
  reportMediaFailure: (failure: MediaDeviceFailure) => void;
  retry: () => void;
  mediaFailure: MediaDeviceFailure | null;
  roomError: Error | null;
  failedBeforeConnecting: boolean;
  /** True only when both orgId and callId are present, i.e. an org user's own seat. */
  canEndSession: boolean;
  requestEndSession: () => void;
  confirmEndSession: () => void;
  cancelEndConfirm: () => void;
  endConfirm: boolean;
  endSessionPending: boolean;
  endSessionError: string | null;
  clearEndSessionError: () => void;
  /** True once this browser's own End session call succeeded, while deferEndAfterEndSession holds the exit open. */
  sessionEndedByMe: boolean;
  finishAfterEndSession: () => void;
};

/**
 * The LiveKit connection lifecycle shared by the old room (VideoRoomPage.tsx) and the new
 * remote-support shell. One implementation so the two cannot drift (D-01).
 *
 * `deferEndAfterEndSession` lets the new shell show an end-of-call prompt after the session is
 * already closed server-side, without the LiveKit room teardown (ROOM_DELETED) racing it shut
 * (D-09). The old room leaves this off and keeps today's immediate onEnded behaviour.
 */
export function useVideoRoomConnection(p: {
  orgId?: string | null;
  callId?: string | null;
  onEnded: (info: VideoRoomEndedInfo) => void;
  deferEndAfterEndSession?: boolean;
  onEndSessionError?: (message: string) => void;
}): VideoRoomConnection {
  const { orgId, callId } = p;

  // Stable identities matter here: LiveKitRoom lists onConnected/onDisconnected/onError in the
  // dependency array of the effect that calls room.connect(), so an inline arrow (or one that
  // closes over p directly) would reconnect the room on every single render. p.onEnded and
  // p.onEndSessionError are read through refs instead, kept current every render.
  const onEndedRef = useRef(p.onEnded);
  onEndedRef.current = p.onEnded;

  const onEndSessionErrorRef = useRef(p.onEndSessionError);
  onEndSessionErrorRef.current = p.onEndSessionError;

  const deferEndAfterEndSession = p.deferEndAfterEndSession === true;

  // Only report an end to the caller once LiveKit actually connected.
  // If connection fails immediately, onDisconnected fires without onConnected -
  // reporting that as ended would run onEnded (window.close for the worker popup)
  // before the user ever saw why it failed.
  const connectedRef = useRef(false);
  const endedInfoRef = useRef<VideoRoomEndedInfo>({ remoteClose: false });

  const [mediaFailure, setMediaFailure] = useState<MediaDeviceFailure | null>(null);
  const [roomError, setRoomError] = useState<Error | null>(null);
  // Set when the room disconnected without ever having connected. Reported
  // rather than acted on - see handleDisconnected.
  const [failedBeforeConnecting, setFailedBeforeConnecting] = useState(false);
  // Bumped to force a remount of LiveKitRoom when the user retries.
  const [attempt, setAttempt] = useState(0);

  const [endConfirm, setEndConfirm] = useState(false);
  const [endSessionError, setEndSessionError] = useState<string | null>(null);

  // Set once this browser's own End session call has closed the session, while
  // deferEndAfterEndSession is holding the exit open for the end-of-call prompt. Mirrored into a
  // ref so the stable handleDisconnected callback can read it without depending on it.
  const [sessionEndedByMe, setSessionEndedByMe] = useState(false);
  const sessionEndedByMeRef = useRef(false);

  // True while this browser's own closeCall request is in flight (deferEndAfterEndSession only).
  // The server deletes the LiveKit room before it answers closeCall, so the ROOM_DELETED
  // disconnect usually arrives before the HTTP response. A disconnect seen while this is set is
  // parked in pendingDisconnectRef and decided once the request settles, instead of being taken
  // for a remote close that shuts the popup before the end-of-call prompt can show.
  const endInFlightRef = useRef(false);
  const pendingDisconnectRef = useRef<VideoRoomEndedInfo | null>(null);

  // Runs a disconnect that was parked while closeCall was in flight, once it turns out the
  // session was not ended by this browser after all.
  const flushPendingDisconnect = () => {
    const pending = pendingDisconnectRef.current;
    pendingDisconnectRef.current = null;
    if (pending != null) {
      onEndedRef.current(pending);
    }
  };

  /**
   * Ends the session for everyone, as opposed to leaving it.
   *
   * fromRoom=true tells the server to excuse this worker's own seat from the "a colleague is
   * in this call" refusal. It cannot excuse anyone else's: the server builds the exempt
   * identity from the authenticated user, not from anything sent here.
   */
  const endSession = useMutation({
    mutationFn: (confirmed: boolean) => {
      if (deferEndAfterEndSession) {
        endInFlightRef.current = true;
      }
      return AdmSupportVideoCallsApi.closeCall(Number(orgId), Number(callId), confirmed, true);
    },
    onSuccess: (r) => {
      endInFlightRef.current = false;
      if (r.requiresConfirmation) {
        setEndConfirm(true);
        // Nothing was closed, so a disconnect that arrived meanwhile was not ours.
        flushPendingDisconnect();
        return;
      }
      setEndConfirm(false);
      if (deferEndAfterEndSession) {
        // Hold the exit open: the server has already closed the session, but the caller still
        // wants to show the end-of-call prompt before running onEnded. A ROOM_DELETED that
        // arrived before this response was the consequence of our own close - drop it.
        pendingDisconnectRef.current = null;
        sessionEndedByMeRef.current = true;
        setSessionEndedByMe(true);
      } else {
        onEndedRef.current({ ...endedInfoRef.current, remoteClose: false });
      }
    },
    onError: (err) => {
      endInFlightRef.current = false;
      if (pendingDisconnectRef.current != null) {
        // The room is already gone while the close failed. There is no room left to show the
        // error in, so run the parked disconnect as it would have run without the request.
        setEndConfirm(false);
        flushPendingDisconnect();
        return;
      }
      // A colleague is still in the call - the server refuses rather than asking, since
      // excusing a colleague is not this worker's call to make. Surface exactly what it
      // says rather than a generic failure, same pattern as elsewhere in the app.
      setEndConfirm(false);
      const message = endSessionErrorMessage(err);
      setEndSessionError(message);
      onEndSessionErrorRef.current?.(message);
    },
  });

  const handleConnected = useCallback(() => {
    connectedRef.current = true;
    setMediaFailure(null);
    setRoomError(null);
    setFailedBeforeConnecting(false);
  }, []);

  const handleDisconnected = useCallback((reason?: DisconnectReason) => {
    // ROOM_DELETED and PARTICIPANT_REMOVED mean the session was ended server-side, by an
    // org user or by the empty-session timer. Anything else is just this browser leaving,
    // and the session carries on without it.
    const remoteClose =
      reason === DisconnectReason.ROOM_DELETED ||
      reason === DisconnectReason.PARTICIPANT_REMOVED;
    endedInfoRef.current = { reason, remoteClose };

    if (!connectedRef.current) {
      // Never connected. Ending here would run onEnded, and for the worker
      // popup that means window.close() - the window blinks open and shuts
      // with no explanation, which is exactly what the user cannot debug.
      // Show why instead, and let them retry.
      setFailedBeforeConnecting(true);
      return;
    }

    if (sessionEndedByMeRef.current) {
      // The server already deleted the LiveKit room as a consequence of this browser's own
      // End session call. The end-of-call prompt is still up (or about to show); the LiveKit
      // teardown must not close the window out from under it. finishAfterEndSession() runs
      // onEnded once the caller is done with the prompt.
      return;
    }

    if (endInFlightRef.current) {
      // This browser's own End session request is still in flight, and the server deletes the
      // LiveKit room before answering it. Decide once the request settles: on success this was
      // our own close and the prompt shows; otherwise the parked disconnect runs as usual.
      pendingDisconnectRef.current = endedInfoRef.current;
      return;
    }

    // Leaving is not ending. No API call.
    onEndedRef.current(endedInfoRef.current);
  }, []);

  // Stable identities matter here: LiveKitRoom lists onError in the dependency
  // array of the effect that calls room.connect(), so an inline arrow makes it
  // reconnect on every single render.
  const handleError = useCallback((err: Error) => {
    setRoomError(err);
  }, []);

  const handleMediaDeviceFailure = useCallback((failure?: MediaDeviceFailure) => {
    if (failure != null) setMediaFailure(failure);
  }, []);

  const retry = useCallback(() => {
    setMediaFailure(null);
    setRoomError(null);
    setFailedBeforeConnecting(false);
    connectedRef.current = false;
    setAttempt((a) => a + 1);
  }, []);

  // Not memoized: endSession.mutate is itself a stable reference (TanStack Query), so these thin
  // wrappers gain nothing from useCallback and are only ever used as onClick handlers, never as
  // a LiveKitRoom prop that needs a stable identity across renders.
  const requestEndSession = () => endSession.mutate(false);
  const confirmEndSession = () => endSession.mutate(true);

  const cancelEndConfirm = useCallback(() => {
    setEndConfirm(false);
  }, []);

  const clearEndSessionError = useCallback(() => {
    setEndSessionError(null);
  }, []);

  const finishAfterEndSession = useCallback(() => {
    onEndedRef.current({ remoteClose: false });
  }, []);

  return {
    attempt,
    handleConnected,
    handleDisconnected,
    handleError,
    handleMediaDeviceFailure,
    reportMediaFailure: setMediaFailure,
    retry,
    mediaFailure,
    roomError,
    failedBeforeConnecting,
    canEndSession: orgId != null && orgId !== "" && callId != null && callId !== "",
    requestEndSession,
    confirmEndSession,
    cancelEndConfirm,
    endConfirm,
    endSessionPending: endSession.isPending,
    endSessionError,
    clearEndSessionError,
    sessionEndedByMe,
    finishAfterEndSession,
  };
}
